// src/pages/Login.js
import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import HeroParticles from '../components/common/HeroParticles'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'

const Login = () => {
    const { login } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [searchParams] = useSearchParams()

    const [loading, setLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        accountType: 'user',
    })

    const [errors, setErrors] = useState({})
    const [alert, setAlert] = useState(null)

    const returnTo = (() => {
        const value = String(searchParams.get('returnTo') || '').trim()
        if (!value || !value.startsWith('/') || value.startsWith('//')) {
            return ''
        }
        return value
    })()

    const handleInputChange = (field) => (event) => {
        const value = event.target.value

        setFormData({
            ...formData,
            [field]: value,
        })

        if (errors[field]) {
            setErrors({ ...errors, [field]: null })
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.email) {
            newErrors.email = isFr ? 'L email est requis' : 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = isFr ? 'L email est invalide' : 'Email is invalid'
        }

        if (!formData.password) {
            newErrors.password = isFr ? 'Le mot de passe est requis' : 'Password is required'
        }

        return newErrors
    }

    const formatLockoutWait = (seconds) => {
        const minutes = Math.max(1, Math.ceil(Number(seconds || 0) / 60))
        return isFr
            ? `${minutes} minute${minutes === 1 ? '' : 's'}`
            : `${minutes} minute${minutes === 1 ? '' : 's'}`
    }

    const loginErrorMessage = (error) => {
        const lockout = error?.response?.data?.lockout
        const rawMessage =
            error?.response?.data?.message ||
            error?.message ||
            ''

        if (lockout?.status === 'temporary') {
            return isFr
                ? `Trop de tentatives de connexion. Reessayez dans ${formatLockoutWait(lockout.seconds_remaining)} ou cliquez sur Mot de passe oublie ? pour reinitialiser votre mot de passe.`
                : `Too many sign-in attempts. Try again in ${formatLockoutWait(lockout.seconds_remaining)} or click Forgot password? to reset your password.`
        }

        if (lockout?.status === 'reset_required') {
            return isFr
                ? 'Trop de tentatives de connexion. Reinitialisez votre mot de passe avec l email du compte ou contactez support@mojopayr.com.'
                : 'Too many sign-in attempts. Reset your password with the email on file or contact support@mojopayr.com.'
        }

        const isInvalidLogin = rawMessage
            .toLowerCase()
            .includes('invalid email')

        if (isInvalidLogin) {
            return isFr
                ? 'Email/telephone ou mot de passe invalide. Verifiez les details ou cliquez sur Mot de passe oublie ? pour le reinitialiser.'
                : 'Invalid email/phone or password. Check the details or click Forgot password? to reset it.'
        }

        return rawMessage || (isFr ? 'Connexion echouee.' : 'Login failed.')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setAlert(null)

        const newErrors = validateForm()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setLoading(true)

        try {
            await login({
                email: formData.email,
                password: formData.password,
                accountType: formData.accountType,
            }, returnTo ? { redirectTo: returnTo } : undefined)
        } catch (error) {
            setAlert({
                type: 'error',
                message: loginErrorMessage(error),
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={{ minHeight: '100vh', position: 'relative' }}>
            <HeroParticles />

            <Container
                maxWidth="sm"
                sx={{ py: 8, position: 'relative', zIndex: 1 }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            bgcolor: 'background.paper',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                        }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography variant="h4" fontWeight="bold">
                                {isFr ? 'Bon retour' : 'Welcome Back'}
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                            >
                                {isFr ? 'Connectez-vous a votre compte' : 'Sign in to your account'}
                            </Typography>
                        </Box>

                        {alert && (
                            <Alert
                                severity={alert.type}
                                onClose={() => setAlert(null)}
                                sx={{ mb: 3 }}
                            >
                                {alert.message}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label={isFr ? 'Adresse email' : 'Email Address'}
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                error={!!errors.email}
                                helperText={errors.email}
                                disabled={loading}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                label={isFr ? 'Mot de passe' : 'Password'}
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={handleInputChange('password')}
                                error={!!errors.password}
                                helperText={errors.password}
                                disabled={loading}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, mt: -2 }}>
                                <Button
                                    component={Link}
                                    to="/forgot-password"
                                    variant="text"
                                    sx={{ color: '#FF8A00', fontWeight: 800, textTransform: 'none' }}
                                >
                                    {isFr ? 'Mot de passe oublie ?' : 'Forgot password?'}
                                </Button>
                            </Box>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>{isFr ? 'Type de compte' : 'Account Type'}</InputLabel>
                                <Select
                                    value={formData.accountType}
                                    label={isFr ? 'Type de compte' : 'Account Type'}
                                    onChange={handleInputChange(
                                        'accountType'
                                    )}
                                >
                                    <MenuItem value="user">
                                        {isFr ? 'Compte utilisateur' : 'User Account'}
                                    </MenuItem>
                                    <MenuItem value="merchant">
                                        {isFr ? 'Compte commercant' : 'Merchant Account'}
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{ mb: 3, py: 1.5 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    (isFr ? 'Connexion' : 'Sign In')
                                )}
                            </Button>
                        </form>

                    </Paper>
                </motion.div>
            </Container>
        </Box>
    )
}

export default Login
